/**
 * Pure presentation templates and date utilities for article listings
 */

/**
 * Format raw date string into "DD Mon YYYY" (e.g. "2026-10-27" -> "27 Oct 2026")
 * @param {string} rawDate
 * @returns {string}
 */
export const formatDisplayDate = (rawDate) => {
    if (!rawDate) return "";
    const months = [
        "Jan", "Feb", "Mar", "Apr", "May", "Jun",
        "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
    ];
    const parts = String(rawDate).split("-");
    if (parts.length < 3) return rawDate;
    const monthIndex = Number(parts[1]) - 1;
    const monthName = months[monthIndex] || parts[1];
    return `${parts[2]} ${monthName} ${parts[0]}`;
};

/**
 * Render single card markup
 * @param {Object} fields - Funnelback response item fields
 * @param {Object} config - Configuration options (e.g. custom badge)
 * @returns {string} HTML string
 */
export const renderListingCard = (fields, config = {}) => {
    const listMeta = fields.listMetadata || {};
    const image =
        (Array.isArray(listMeta.image) ? listMeta.image[0] : listMeta.image) ||
        fields.profileImage ||
        "/?a=235271";

    const title =
        fields.title ||
        (Array.isArray(listMeta.t) ? listMeta.t[0] : listMeta.t) ||
        "";

    const rawDate =
        (Array.isArray(listMeta.d) ? listMeta.d[0] : listMeta.d) ||
        fields.date ||
        "";
    const formattedDate = formatDisplayDate(rawDate);

    const url = fields.liveUrl || fields.displayUrl || fields.url || "#";

    let rawPageType =
        (Array.isArray(listMeta.pageType) ? listMeta.pageType[0] : listMeta.pageType) ||
        "";
    let badge = "";
    if (rawPageType === "News - Article") {
        badge = "News article";
    } else if (rawPageType === "News - Press release") {
        badge = "Press release";
    } else if (rawPageType) {
        badge = rawPageType.replace(/\w*\s-\s/gm, "");
    }
    if (config.badge) {
        badge = config.badge;
    }

    return `
        <a href="${url}" class="cards__card-wrapper cards__card-wrapper--news date-mobile">
            <article class="cards__card row no-gutters flex-column justify-content-between flex-wrap">
                ${badge ? `
                <div class="cards__flag-wrapper">
                    <div class="cards__flag-type">${badge}</div>
                </div>` : ""}
                <div class="cards__image-wrapper">
                    <div class="cards__image"><img src="${image}" alt="" loading="lazy"></div>
                </div>
                <div class="cards__content no-gutters flex-column justify-content-between">
                    <div class="cards__title-wrapper">
                        <p class="cards__title truncate-lines-2">${title}</p>
                    </div>
                    ${formattedDate ? `<p class="cards__publish-date-mobile">${formattedDate}</p>` : ""}
                </div>
            </article>
        </a>`;
};

/**
 * Render search input form
 * @param {string} query
 * @param {string} inputId
 * @param {string} placeholder
 * @returns {string} HTML string
 */
export const renderSearchForm = (query, inputId, placeholder = "Search") => {
    const cleanQuery = query === "!nullsearch" ? "" : (query || "");
    return `
        <div class="search-field">
            <form class="search__form" role="search" action="/search" method="get">
                <label for="${inputId}" class="sr-only">${placeholder}</label>
                <input
                    id="${inputId}"
                    type="text"
                    class="search__form-input search-custom"
                    placeholder="${placeholder}"
                    autocomplete="off"
                    role="searchbox"
                    aria-label="Search"
                    autocorrect="off"
                    autocapitalize="off"
                    spellcheck="false"
                    maxlength="1000"
                    value="${cleanQuery}">
                <button type="submit" name="site-search-submit" class="search__submit">
                    <i class="search__submit-icon" aria-hidden="true"></i>
                    <span class="sr-only">Search</span>
                </button>
                <div class="fb-search-wrapper"></div>
            </form>
        </div>`;
};

/**
 * Render "Load more" button
 * @returns {string} HTML string
 */
export const renderLoadMore = () => `
    <div class="landing-page__button-wrapper">
        <button id="search-loadmore" type="button" title="Load more results" class="btn btn--secondary">
            Load more
        </button>
    </div>`;

/**
 * Render empty results message
 * @param {boolean} hasActiveFilters
 * @returns {string} HTML string
 */
export const renderNoResults = (hasActiveFilters) => {
    if (hasActiveFilters) {
        return `<div>There are no results matching your filter criteria</div>`;
    }
    return `<div>We're sorry, we couldn't find anything matching</div>`;
};
